import Base from '../paperTool/PaperTool'
import { IMasterSetPlanToPost } from '../../../../../../models/masterSetPlan'
import { OVERLAY_RASTER_ITEM_NAME } from '../../../../../../shared/constants/drawable-types'
import { initialToolsState } from '../../../../../slices/tools'
import { Coordinates2D, ItemScale, PaperToolConfig, Vector2D } from '../../../../../types'

export class Overlay extends Base {
    public static NAME = 'Overlay'
    public static DEFAULT_OPACITY = 0.6

    private static RASTER_CROSSHAIR_GROUP_NAME = 'raster-crosshair'
    private static BOUND_NAMES = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight']
    private static BOUND_NAME_OPPOSITE_CORNER: Record<string, string> = {
        topLeft: 'bottomRight',
        topRight: 'bottomLeft',
        bottomLeft: 'topRight',
        bottomRight: 'topLeft',
    }
    private static CROSS_HAIR_FILL_COLOR = 'white'
    private static CROSS_HAIR_STROKE_COLOR = '#0086C9'

    private paperRastersDict: Record<number, paper.Raster> = {}
    private paperRastersSourceDict: Record<number, string> = {}
    private selectedImage: paper.Raster | null = null
    private selectedImageBackground: paper.Item | null = null
    private selectedImageCrossHairs: paper.Item | null = null
    private selectedImageCrossHairParent: paper.Group | null = null
    private originalImageSizeDictionary: Record<number, paper.Size> = {}
    private rasterBackgroundDict: Record<number, paper.Path.Rectangle> = {}
    private croppingStartingPoint: paper.Point | null = null
    private croppingEndingPoints: paper.Point | null = null
    private croppingRectangle: paper.Path | null = null
    private croppingCoordinates: Coordinates2D = []
    private croppingPositionChangeVector: paper.Point | null = null

    public isLocked = false

    constructor(config: PaperToolConfig) {
        super(config)
        this.name = Overlay.NAME
    }

    private updateCrosshairPositions = (imageRaster: paper.Raster) => {
        const { bounds } = imageRaster

        this.selectedImageCrossHairParent?.children.forEach((corner) => {
            corner.position = bounds[corner.data.boundName]
        })
    }

    private placeImageRasterInTopRightCorner = (imageRaster: paper.Raster) => {
        // places the center of the overlay in the top right corner of the underlying plan
        const mainRaster = this.paper.project.activeLayer.children.find(
            (item) => item.data.isMainRaster
        ) as paper.Raster

        imageRaster.position = new this.paper.Point(mainRaster.width, 0)
    }

    private loadImageRaster({
        source,
        id,
        opacity,
        shouldSkipLoading,
    }: {
        id: number
        source: string
        opacity: number
        shouldSkipLoading: boolean
    }) {
        return new Promise<paper.Raster>((resolve) => {
            const raster = new this.paper.Raster({
                source: source,
                blendMode: 'multiply',
            })

            raster.name = OVERLAY_RASTER_ITEM_NAME
            raster.data.id = id
            raster.opacity = opacity
            raster.selectedColor = new this.paper.Color(Overlay.CROSS_HAIR_STROKE_COLOR)
            raster.data.isEnabledForBorderTool = false

            if (shouldSkipLoading) {
                resolve(raster)
            }

            raster.onLoad = (): void => {
                this.sendItemOnlyOnTopOfMainRaster(raster)
                resolve(raster)
            }
        })
    }

    private updateImageMetaDataAndBackground({
        id,
        raster,
        opacity,
        bgFillColour,
        isRasterCroppable,
    }: {
        id: number
        raster: paper.Raster
        opacity: number
        bgFillColour: paper.Color
        isRasterCroppable: boolean
    }) {
        raster.name = OVERLAY_RASTER_ITEM_NAME
        raster.data.id = id
        raster.opacity = opacity
        raster.selectedColor = new this.paper.Color(Overlay.CROSS_HAIR_STROKE_COLOR)
        raster.data.isCroppable = isRasterCroppable
        raster.data.isEnabledForBorderTool = false
        // onSelect lets Select.tool.ts pick this up generically. shapeEditable/movable keep a
        // locked overlay selectable but immovable.
        raster.data.onSelect = () => this.mediator.mediate('2D', { selectedOverlayChunkId: id })
        raster.data.shapeEditable = false
        raster.data.movable = false

        const newRasterBackground = new this.paper.Path.Rectangle({
            rectangle: raster.bounds,
            fillColor: bgFillColour,
            position: raster.position,
            opacity: raster.opacity,
        })

        newRasterBackground.data.isEnabledForBorderTool = false
        newRasterBackground.insertBelow(raster)

        this.rasterBackgroundDict[id]?.remove()

        this.originalImageSizeDictionary[id] = raster.size
        this.paperRastersDict[id] = raster
        this.rasterBackgroundDict[id] = newRasterBackground
        this.updateCrosshairPositions(raster)
    }

    private crop(id: number, cropStartPoint: paper.Point, cropEndPoint: paper.Point) {
        const imageRaster = this.paperRastersDict[id]

        if (imageRaster.data.isCroppable) {
            const rasterBackground = this.rasterBackgroundDict[id]
            const imageRasterBounds = imageRaster.bounds

            const cropStartPointRelativeToTopLeftOfOverlay = new this.paper.Point(
                cropStartPoint.x - imageRasterBounds.topLeft.x,
                cropStartPoint.y - imageRasterBounds.topLeft.y
            )

            const cropEndPointRelativeToTopLeftOfOverlay = new this.paper.Point(
                cropEndPoint.x - imageRasterBounds.topLeft.x,
                cropEndPoint.y - imageRasterBounds.topLeft.y
            )
            const newBounds = new this.paper.Rectangle(
                cropStartPointRelativeToTopLeftOfOverlay,
                cropEndPointRelativeToTopLeftOfOverlay
            )
            const subRaster = imageRaster.getSubRaster(newBounds)

            this.updateImageMetaDataAndBackground({
                id,
                raster: subRaster,
                opacity: imageRaster.opacity,
                bgFillColour: rasterBackground.fillColor!,
                isRasterCroppable: false,
            })

            this.selectedImage = subRaster
            this.croppingCoordinates = new this.paper.Path.Rectangle(newBounds).segments.map(({ point }) => [
                point.x,
                point.y,
            ])

            this.croppingPositionChangeVector = new this.paper.Point(
                subRaster.position.x - imageRaster.position.x,
                subRaster.position.y - imageRaster.position.y
            )

            rasterBackground.remove()
            imageRaster.remove()
        }
    }

    private removeOverlay = (id: number) => {
        const imageRaster = this.paperRastersDict[id]
        const rasterBackground = this.rasterBackgroundDict[id]

        if (!imageRaster || !rasterBackground) return

        imageRaster.remove()
        rasterBackground.remove()
        delete this.paperRastersDict[id]
        delete this.rasterBackgroundDict[id]
        delete this.paperRastersSourceDict[id]
        this.croppingCoordinates = []
        this.croppingRectangle?.remove()
    }

    private createSelectionCrossHairsForImage = () => {
        if (!this.selectedImage) return

        const bounds = this.selectedImage.bounds

        const boundingPointsParentGroup = new this.paper.Group()

        const boundItems = Overlay.BOUND_NAMES.map((boundName) => {
            const boundPoint = bounds[boundName] as paper.Point
            const crossHairCircle = new this.paper.Path.Circle(boundPoint, this.calculateHandleRadiusAtCurrentZoom())

            crossHairCircle.fillColor = new this.paper.Color(Overlay.CROSS_HAIR_FILL_COLOR)
            crossHairCircle.strokeColor = new this.paper.Color(Overlay.CROSS_HAIR_STROKE_COLOR)
            crossHairCircle.data = {
                boundName,
            }
            crossHairCircle.name = Overlay.RASTER_CROSSHAIR_GROUP_NAME

            return crossHairCircle
        })

        boundingPointsParentGroup.addChildren(boundItems)
        this.selectedImageCrossHairParent = boundingPointsParentGroup
    }

    private limitHitResultToRasterImages = (hitResult: paper.HitResult) => {
        return (
            (this.selectedImage === null && hitResult.item.name === OVERLAY_RASTER_ITEM_NAME) ||
            (this.selectedImage !== null && hitResult.item.name === Overlay.RASTER_CROSSHAIR_GROUP_NAME)
        )
    }

    private createAndUpdateCroppingRectangle(fromPoint: paper.Point, toPoint: paper.Point) {
        if (this.croppingRectangle) {
            this.croppingRectangle.remove()
        }

        this.croppingRectangle = new this.paper.Path.Rectangle(fromPoint, toPoint)

        this.croppingRectangle.strokeColor = new this.paper.Color('white')
        this.croppingRectangle.opacity = 1

        this.croppingRectangle.strokeWidth = this.strokeWidth || initialToolsState.strokeWidth // if the stroke is not set, then set it to a default value
        this.croppingRectangle.strokeWidth *= ItemScale.LINE
        this.croppingRectangle.fillColor = null
        this.croppingRectangle.data.selectable = true

        // these are multiplied to scale the dashes based on the stroke width (the default numbers in the store are based on 1px width)
        this.croppingRectangle.dashArray = [
            initialToolsState.dashArray[0] * this.croppingRectangle.strokeWidth,
            initialToolsState.dashArray[1] * this.croppingRectangle.strokeWidth,
        ]
    }

    public insertOverlay = async (
        source: string,
        id: number,
        opacity: number,
        overlayColor: string,
        isCroppable = false,
        shouldSkipLoading = false
    ) => {
        if (this.paperRastersDict[id]) return

        const imageRaster = await this.loadImageRaster({
            source,
            id,
            opacity,
            shouldSkipLoading,
        })

        this.placeImageRasterInTopRightCorner(imageRaster)

        this.updateImageMetaDataAndBackground({
            id,
            raster: imageRaster,
            opacity,
            bgFillColour: new this.paper.Color(overlayColor),
            isRasterCroppable: isCroppable,
        })

        this.paperRastersSourceDict[id] = source
    }

    public extractCroppingAPIData = (id: number): IMasterSetPlanToPost['plans']['0']['chunkToCropAndOverlay'] => {
        const imageRaster = this.paperRastersDict[id]
        // Check that the image is not croppable and that we have cropping
        // coordinates indicating that the current image has been cropped

        if (!imageRaster.data.isCroppable && this.croppingCoordinates.length !== 0) {
            return {
                chunkId: id,
                cropCoordinates: this.croppingCoordinates,
                overlayCoordinates: new this.paper.Path.Rectangle(imageRaster.bounds).segments.map(({ point }) => [
                    point.x,
                    point.y,
                ]),
                scale: this.extractOverlayScaleData(id),
            }
        }

        return undefined
    }

    private extractOverlayScaleData = (id: number) => {
        const imageRaster = this.paperRastersDict[id]

        if (!imageRaster) return 1

        const originalImageSize = this.originalImageSizeDictionary[id]

        if (!originalImageSize) return 1

        return imageRaster.bounds.width / originalImageSize.width
    }

    public rotateImageRaster = (id: number, angleChange: number) => {
        const imageRaster = this.paperRastersDict[id]
        const rasterBackground = this.rasterBackgroundDict[id]

        if (!imageRaster || !rasterBackground) return
        imageRaster.rotate(angleChange)
        rasterBackground.rotate(angleChange)
        this.updateCrosshairPositions(imageRaster)
    }

    public resetOverlayPosition = (id: number) => {
        const imageRaster = this.paperRastersDict[id]
        const rasterBackground = this.rasterBackgroundDict[id]

        if (!imageRaster || !rasterBackground) return
        this.placeImageRasterInTopRightCorner(imageRaster)
        rasterBackground.position = imageRaster.position
        this.updateCrosshairPositions(imageRaster)
    }

    public cancel = () => {
        Object.entries(this.paperRastersDict).forEach(([_, raster]) => {
            raster.selected = false
            raster.locked = true
        })

        this.selectedImageCrossHairParent?.removeChildren()
        this.selectedImageCrossHairParent?.remove()
        this.selectedImageCrossHairParent = null
        this.selectedImageCrossHairs = null
        this.selectedImage = null
        this.croppingRectangle?.remove()
        this.croppingRectangle = null
    }

    public resetOverlayRasters = () => {
        for (const id in this.paperRastersDict) {
            if (this.paperRastersDict.hasOwnProperty(id)) {
                this.removeOverlay(parseInt(id))
            }
        }
    }

    public removeOverlayById = (id: number) => {
        this.removeOverlay(id)
    }

    public changeOverlayOpacity = (id: number, newOpacity: number) => {
        if (!isFinite(newOpacity) || newOpacity < 0 || newOpacity > 1) return
        const imageRaster = this.paperRastersDict[id]
        const rasterBackground = this.rasterBackgroundDict[id]

        if (!imageRaster || !rasterBackground) return
        imageRaster.opacity = newOpacity
        rasterBackground.opacity = newOpacity
    }

    public changeOverlayColor = (id: number, newColor: string) => {
        const imageRaster = this.paperRastersDict[id]
        const rasterBackground = this.rasterBackgroundDict[id]

        if (!imageRaster || !rasterBackground) return
        rasterBackground.fillColor = new this.paper.Color(newColor)
    }

    public resetSelectedImagesAndCrossHairs = () => {
        if (this.selectedImage) this.selectedImage.selected = false
        this.selectedImage = null
        this.selectedImageBackground = null
        if (this.selectedImageCrossHairs) {
            this.selectedImageCrossHairs?.parent?.removeChildren()
            this.selectedImageCrossHairs?.parent?.remove()
            this.selectedImageCrossHairs = null
            this.selectedImageCrossHairParent = null
        } else {
            this.selectedImageCrossHairParent?.removeChildren()
            this.selectedImageCrossHairParent?.remove()
            this.selectedImageCrossHairParent = null
        }
    }

    public onMouseDown = (event: paper.ToolEvent): void => {
        if (this.isPanningClick(event) || this.isLocked) return

        if (event.modifiers.shift) {
            this.croppingStartingPoint = event.point
            this.createAndUpdateCroppingRectangle(this.croppingStartingPoint, this.croppingStartingPoint)

            return
        }

        const hitResults = this.getPaperScope().project.hitTestAll(event.point)

        const hitResult = hitResults.filter(this.limitHitResultToRasterImages)[0]

        if (!hitResult || (hitResult !== null && !this.limitHitResultToRasterImages(hitResult))) {
            this.resetSelectedImagesAndCrossHairs()

            return
        }

        const { item } = hitResult

        if (item.name === OVERLAY_RASTER_ITEM_NAME) {
            this.selectedImage = item as paper.Raster
            this.selectedImage.selected = true
            this.selectedImageBackground = this.rasterBackgroundDict[item.data.id]
            if (!this.selectedImageCrossHairParent) this.createSelectionCrossHairsForImage()
            this.mediator.mediate('2D', { selectedOverlayChunkId: item.data.id })
        } else if (item.name === Overlay.RASTER_CROSSHAIR_GROUP_NAME) {
            this.selectedImageCrossHairs = item
        }
    }

    public onMouseDrag = (event: paper.ToolEvent): void => {
        if (this.toolPanning(event) || this.isLocked) return

        const { delta } = event

        if (event.modifiers.shift) {
            this.croppingEndingPoints = event.point
            if (this.croppingRectangle && this.croppingStartingPoint) {
                this.createAndUpdateCroppingRectangle(this.croppingStartingPoint, this.croppingEndingPoints)
            }

            return
        }

        if (this.selectedImageCrossHairs && this.selectedImage && this.selectedImageBackground) {
            const oppositePoint = this.selectedImageCrossHairs?.parent.children.find(
                (item) =>
                    item.data.boundName ===
                    Overlay.BOUND_NAME_OPPOSITE_CORNER[this.selectedImageCrossHairs?.data.boundName]
            )

            if (!oppositePoint) return

            const newCorner = this.selectedImageCrossHairs.position.add(delta)
            const originalSize = this.selectedImageCrossHairs.position.subtract(oppositePoint.position)
            const aspectRatio = originalSize.x / originalSize.y
            const newSize = newCorner.subtract(oppositePoint.position)

            this.selectedImage.scale(
                newSize.x / originalSize.x,
                newSize.x / aspectRatio / originalSize.y,
                oppositePoint.position
            )
            this.selectedImageBackground.scale(
                newSize.x / originalSize.x,
                newSize.x / aspectRatio / originalSize.y,
                oppositePoint.position
            )
            this.updateCrosshairPositions(this.selectedImage)
        } else if (this.selectedImage && this.selectedImageBackground) {
            this.selectedImage.position = this.selectedImage.position.add(delta)
            this.selectedImageBackground.position = this.selectedImageBackground.position.add(delta)
            this.selectedImageCrossHairParent?.removeChildren()
            this.selectedImageCrossHairParent?.remove()
            this.selectedImageCrossHairParent = null
            this.createSelectionCrossHairsForImage()
        }
    }

    public onMouseUp = () => {
        if (this.croppingEndingPoints && this.croppingStartingPoint && this.selectedImage) {
            this.crop(this.selectedImage.data.id, this.croppingStartingPoint, this.croppingEndingPoints)
        }
        this.croppingRectangle?.remove()
        this.croppingEndingPoints = null
        this.croppingStartingPoint = null
        this.mediator.mediate('common', { cursor: 'default' })
    }

    public onKeyDown = async (keyEvent: any) => {
        if (keyEvent.character === 'z' && keyEvent.modifiers.control) {
            if (this.croppingCoordinates.length > 0 && this.selectedImage && this.croppingPositionChangeVector) {
                const id = this.selectedImage.data.id
                const originalSource = this.paperRastersSourceDict[id]
                const originalBackground = this.rasterBackgroundDict[id]

                if (originalSource && originalBackground) {
                    this.croppingCoordinates = []

                    const imageRaster = await this.loadImageRaster({
                        source: originalSource,
                        id,
                        opacity: this.selectedImage!.opacity,
                        shouldSkipLoading: false,
                    })

                    const newOrigin = new this.paper.Point(
                        this.selectedImage.position.x - this.croppingPositionChangeVector.x,
                        this.selectedImage.position.y - this.croppingPositionChangeVector.y
                    )

                    imageRaster.position = newOrigin

                    this.updateImageMetaDataAndBackground({
                        raster: imageRaster,
                        bgFillColour: originalBackground.fillColor!,
                        isRasterCroppable: true,
                        opacity: this.selectedImage.opacity,
                        id: this.selectedImage.data.id,
                    })

                    this.selectedImage.remove()
                    this.selectedImage = imageRaster
                    this.croppingPositionChangeVector = null
                    originalBackground.remove()
                }
            }

            return
        }

        const overlayAdditionPoint = this.determinePointToTranslate(keyEvent.event.code)

        if (this.selectedImage && this.selectedImageBackground) {
            this.selectedImage.position = this.selectedImage.position.add(overlayAdditionPoint)
            this.selectedImageBackground.position = this.selectedImageBackground.position.add(overlayAdditionPoint)
            this.selectedImageCrossHairParent?.removeChildren()
            this.selectedImageCrossHairParent?.remove()
            this.selectedImageCrossHairParent = null
            this.createSelectionCrossHairsForImage()
        }
    }

    public extractOverlayDataToSave = (id: number) => {
        const imageRaster = this.paperRastersDict[id]
        const imageRasterOriginalData = this.originalImageSizeDictionary[id]

        if (!imageRaster || !imageRasterOriginalData) return null

        const inverseRotatedImageRaster = imageRaster.clone()

        // invert the rotation operation so that we can compare
        // widths to determine scale
        inverseRotatedImageRaster.rotate(-1 * imageRaster.rotation)

        return {
            scale: inverseRotatedImageRaster.bounds.size.width / imageRasterOriginalData.width,
            rotation: imageRaster.rotation > 0 ? imageRaster.rotation : 360 - Math.abs(imageRaster.rotation),
            position: [imageRaster.position.x, imageRaster.position.y] as Vector2D,
        }
    }

    public isPathInsideOverlay = (pathItem: paper.Item, id: number) => {
        const imageRaster = this.paperRastersDict[id]

        if (!imageRaster) throw new Error('Option raster not found. Aborting')

        return (
            pathItem.intersects(imageRaster) ||
            pathItem.isInside(imageRaster.bounds) ||
            imageRaster.isInside(pathItem.bounds)
        )
    }

    public toggleVisibilityOfOverlayById = (id: number) => {
        const imageRaster = this.paperRastersDict[id]
        const rasterBackground = this.rasterBackgroundDict[id]

        if (!imageRaster || !rasterBackground) return

        imageRaster.visible = !imageRaster.visible
        rasterBackground.visible = !rasterBackground.visible
    }

    public setOverlayPositionCoordinates = (id: number, coordinates: Vector2D) => {
        const imageRaster = this.paperRastersDict[id]
        const rasterBackground = this.rasterBackgroundDict[id]

        if (!imageRaster || !rasterBackground) return

        imageRaster.position = new this.paper.Point(coordinates)
        rasterBackground.position = new this.paper.Point(coordinates)
    }

    public lockOverlay = (shouldLock: boolean) => {
        this.isLocked = shouldLock
    }

    public onActivate = () => {
        Object.entries(this.paperRastersDict).forEach(([_, raster]) => {
            raster.locked = false
        })
    }

    public updateOverlayScale = (overlayId: number, newScale: number) => {
        const raster = this.paperRastersDict[overlayId]
        const rasterBackground = this.rasterBackgroundDict[overlayId]

        if (raster) {
            raster.scale(newScale)
        }

        if (rasterBackground) {
            rasterBackground.scale(newScale)
        }
    }
}
